import React from 'react';
import { pick } from 'lodash';
import { Button, Card, CardContent, CardHeader, Typography } from '@mui/material';

import { SegmentFragment, SegmentUpdate } from '@occc/graphql';
import { useFormSubmit } from '@occc-app/hooks/useFormSubmit';
import { useUpdateSegment } from '@occc/feat-segments';
import { useOpenState } from '@occc-app/hooks/useOpenState';
import { ConfirmDialog } from '@occc/ui-design-system';

import { SegmentForm } from '../components/SegmentForm';

export const updateSegmentFormId = 'updateSegmentForm';

type EditSegmentModalContainerProps = {
  children?: React.ReactNode;
  segment: SegmentFragment | null;
  isUserPermittedToEditSegment: boolean;
};

const EditSegmentModalContainer = (props: EditSegmentModalContainerProps) => {
  const { segment, isUserPermittedToEditSegment } = props;

  const initialValues = pick(segment, ['name', 'description', 'shortCode']);
  const [open, onOpen, onClose] = useOpenState();
  const openEditModal = React.useCallback(() => {
    if (!segment) {
      return;
    }
    onOpen();
  }, [segment, onOpen]);
  const [segmentUpdate] = useUpdateSegment({
    refetchQueries: ['segment'],
  });
  const submitUpdateSegmentForm = useFormSubmit(updateSegmentFormId);

  const onSubmit = ({ name, description }: SegmentUpdate): void => {
    if (segment) {
      segmentUpdate({
        variables: {
          id: segment.id,
          input: { name, description },
        },
      });
      onClose();
    }
  };

  return (
    <>
      <Card>
        <CardHeader
          title={
            <Typography variant='h6' component='h3'>
              General Details
            </Typography>
          }
          action={
            isUserPermittedToEditSegment && (
              <Button variant='text' color='primary' onClick={openEditModal}>
                Edit
              </Button>
            )
          }
        />
        <CardContent>{props.children}</CardContent>
      </Card>
      <ConfirmDialog
        dialogTitle='Edit Segment'
        open={open}
        onClose={onClose}
        fullWidth={true}
        confirmButtonText='Save'
        confirmButtonProps={{ onClick: submitUpdateSegmentForm }}
      >
        <SegmentForm
          id={updateSegmentFormId}
          readOnly={false}
          editMode={open}
          onSubmit={onSubmit}
          initialValues={initialValues as SegmentFragment}
        />
      </ConfirmDialog>
    </>
  );
};

export default EditSegmentModalContainer;
